import { formatMoney } from 'accounting'
import { DateTime } from 'luxon'

import {
  Box,
  Button,
  Line,
  SidebarModal,
  SidebarNoticeBox,
  Text,
} from '@domain-group/suit-theme'

import useCampaignQuoteTemplateItem, { useCampaignQuoteTemplateItemForm } from '@hooks/useCampaignQuoteTemplateItem'

import ProviderSubHeader from '@components/ProviderSubHeader'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'
import type { CampaignQuoteTemplateItemModel } from '@models/types'

const MODAL_KEY = 'PriceAutoUpdatedModal'

type ModalCallbacks = {
  closeModal: () => void,
  updateCampaignQuoteTemplateItem: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  campaignQuoteTemplateItem?: Partial<CampaignQuoteTemplateItemModel>,
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const PriceAutoUpdatedModal = (props: ModalProps) => {
  const { campaignQuoteTemplateItem, callbacks, showModal } = props
  const { closeModal, updateCampaignQuoteTemplateItem } = callbacks

  const quoteItemPayload = useCampaignQuoteTemplateItem(campaignQuoteTemplateItem || {})

  const { callbacks: { updateCampaignQuoteTemplateItem: updateFn }, updating } = quoteItemPayload || {}

  const { entityState, setEntityState } = useCampaignQuoteTemplateItemForm(campaignQuoteTemplateItem || {})

  if (!campaignQuoteTemplateItem){
    return null
  }

  const priceFrom = campaignQuoteTemplateItem.data?.auto_price_update?.from
  const priceTo = campaignQuoteTemplateItem.data?.auto_price_update?.to
  const updatedAt = campaignQuoteTemplateItem.data?.auto_price_update?.at
  const title = campaignQuoteTemplateItem?.display_title
  const { provider_id } = campaignQuoteTemplateItem || {}

  const formattedDate = updatedAt
    ? DateTime.fromISO(updatedAt).toFormat('MMM d, yyyy h:mm a')
    : 'Unknown'

  const priceDifference = typeof priceFrom === 'number' && typeof priceTo === 'number'
    ? priceTo - priceFrom
    : null
  const percentChange = typeof priceFrom === 'number' && priceFrom !== 0
    ? ((priceDifference || 0) / priceFrom) * 100
    : null

  const handleMarkAsSeen = () => {
    const updatedParams = {
      ...entityState,
      data: {
        ...entityState.data,
        auto_price_update: {
          ...entityState.data?.auto_price_update,
          seen: true,
        },
      },
    }
    setEntityState(updatedParams)

    const payload: HandleCallbackActionParams = {
      callbacks: {
        action: updateFn,
        afterAction: closeModal,
      },
      entityParams: updatedParams,
      toastText: 'Marked as Seen',
    }

    updateCampaignQuoteTemplateItem(payload)
  }
  return (
    <SidebarModal
      callbacks={callbacks}
      modalKey={MODAL_KEY}
      showModal={showModal}
    >
      <SidebarModal.Header title="Price" titleSecondLine="Updated" />

      {provider_id && <ProviderSubHeader providerId={provider_id} secondaryText={title || 'Unknown'} />}

      <SidebarModal.Content>
        <SidebarNoticeBox>
          <SidebarNoticeBox.Title>This Item&apos;s price has changed</SidebarNoticeBox.Title>

          <SidebarNoticeBox.Paragraph>
            The provider has updated the price of this price list item. See the details below.
          </SidebarNoticeBox.Paragraph>
        </SidebarNoticeBox>

        <Box marginBottom="medium">
          <Text fontSize="small" color="bodyFontLightColor">
            Updated At:
          </Text>

          <Text fontSize="small" marginLeft="small" color="bodyFontColor">
            {formattedDate}
          </Text>
        </Box>

        <Line />

        <Box>
          <Box marginBottom="small">
            <Text fontSize="small" color="bodyFontLightColor" marginBottom={1}>
              Prev Price:
            </Text>

            <Text fontSize="small" marginLeft="small" color="bodyFontColor">
              {typeof priceFrom === 'number' ? formatMoney(priceFrom) : 'N/A'}
            </Text>
          </Box>

          <Box>
            <Text fontSize="small" color="bodyFontLightColor">
              New Price:
            </Text>

            <Text fontSize="small" marginLeft="small" color="bodyFontColor">
              {typeof priceTo === 'number' ? formatMoney(priceTo) : 'N/A'}
            </Text>
          </Box>
        </Box>

        {priceDifference !== null && (
          <>
            <Line />

            <Box marginTop="small">
              <Text fontSize="small" color="bodyFontLightColor" marginBottom="small">
                Change:
              </Text>

              <Box marginLeft="small" alignItems="center">
                <Text fontSize="small">
                  {priceDifference < 0 ? '−' : '+'}
                  {formatMoney(Math.abs(priceDifference))}
                </Text>

                {percentChange !== null && (
                  <Text fontSize="small">
                    ({priceDifference < 0 ? '−' : '+'}
                    {percentChange.toFixed(1)}
                    %)
                  </Text>
                )}
              </Box>
            </Box>
          </>
        )}
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={updating}
          marginLeft="small"
          onClick={handleMarkAsSeen}
          size="large"
        >
          Mark as Seen
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

export default PriceAutoUpdatedModal
